import { normalizedConversationPhone } from "./conversationIdentity.mjs";

const CLIENT_ACTORS = new Set(["client", "cliente", "customer", "user"]);
const HUMAN_ACTORS = new Set(["consultant", "consultor", "human"]);

function timestampOf(message) {
  const timestamp = Date.parse(
    message?.timestamp || message?.sentAt || message?.acceptedAt || message?.createdAt || "",
  );
  return Number.isFinite(timestamp) ? timestamp : NaN;
}

function messageIdOf(message, fallbackId) {
  const id = message?.id ?? message?.crmMessageId ?? message?.waMessageId
    ?? message?.providerMessageId ?? fallbackId;
  return id == null ? "" : String(id);
}

function isHumanSender(message) {
  const actor = String(message?.actorType || message?.remetente || message?.role || "")
    .trim().toLowerCase();
  return HUMAN_ACTORS.has(actor)
    || (message?.fromMe === true && message?.manual_physical === true);
}

export function isConfirmedHumanOutbound(message) {
  if (!isHumanSender(message)) return false;
  const status = String(message?.status || "").trim().toLowerCase();
  return ["sent", "accepted", "delivered", "read"].includes(status)
    || message?.provider_accepted === true
    || Number(message?.waAck) > 0;
}

export function isGenuineClientInbound(message) {
  if (message?.directionInferred === true || message?.senderInferred === true) return false;
  const actor = String(message?.actorType || message?.remetente || message?.responsavel
    || message?.role || "").trim().toLowerCase();
  return CLIENT_ACTORS.has(actor)
    || (message?.fromMe === false && message?.directionConfirmed === true);
}

export function hasConfirmedProspectOutbound(state) {
  const cycle = state?.prospectCycle;
  return cycle?.evidenceVersion === 1
    && Boolean(String(cycle.firstOutboundEventId || "").trim())
    && Number.isFinite(Date.parse(cycle.firstOutboundAt || ""));
}

export function hasCurrentProspectReply(state) {
  const cycle = state?.prospectCycle;
  if (!hasConfirmedProspectOutbound(state)
    || !String(cycle.replyEventId || "").trim()
    || !CLIENT_ACTORS.has(String(cycle.replyActorType || "").trim().toLowerCase())) return false;
  const outboundAt = Date.parse(cycle.firstOutboundAt);
  const replyAt = Date.parse(cycle.replyAt || "");
  return Number.isFinite(replyAt) && replyAt > outboundAt;
}

export function confirmedReminderTarget(reminder, prospect) {
  const prospectStoreId = Number(prospect?.storeId);
  const reminderStoreId = Number(reminder?.storeId);
  const channelStoreId = Number(reminder?.channelStoreId);
  const phone = normalizedConversationPhone(prospect?.phone || prospect?.telefone);
  const reminderPhone = normalizedConversationPhone(reminder?.phone || reminder?.telefone);
  const outboundMessageId = String(reminder?.outboundMessageId || "").trim();
  if (prospect?.id == null || String(reminder?.prospId) !== String(prospect.id)
    || !Number.isInteger(prospectStoreId) || prospectStoreId <= 0
    || reminderStoreId !== prospectStoreId
    || !Number.isInteger(channelStoreId) || channelStoreId <= 0
    || !phone || reminderPhone !== phone || !outboundMessageId) return null;
  return { prospectStoreId, channelStoreId, phone, outboundMessageId };
}

// `messages` carries tenant-qualified identity alongside each message. The
// caller must not flatten unscoped phone-only history into this list.
export function hasReplyToConfirmedProspectOutbound({
  messages,
  storeId,
  phone,
  outboundMessageId,
  cycleStartedAt = null,
}) {
  const expectedStore = Number(storeId);
  const expectedPhone = normalizedConversationPhone(phone);
  const expectedKey = `${expectedStore}:${expectedPhone}`;
  const outboundId = String(outboundMessageId || "").trim();
  if (!Number.isInteger(expectedStore) || expectedStore <= 0 || !expectedPhone || !outboundId) return false;

  const scoped = (Array.isArray(messages) ? messages : []).filter(entry => {
    const entryStore = Number(entry?.storeId);
    const entryPhone = normalizedConversationPhone(entry?.phone);
    return `${entryStore}:${entryPhone}` === expectedKey;
  });
  const uniqueById = new Map();
  const ambiguousIds = new Set();
  scoped.forEach(entry => {
    const id = messageIdOf(entry.message, entry.id);
    if (!id) return;
    const message = entry.message || {};
    const signature = JSON.stringify({
      sender:message.actorType || message.remetente || message.responsavel || message.role || "",
      timestamp:timestampOf(message),
      status:String(message.status || "").toLowerCase(),
      providerAccepted:message.provider_accepted === true,
      waAck:Number(message.waAck) || 0,
      directionInferred:message.directionInferred === true,
    });
    const previous = uniqueById.get(id);
    if (previous && previous.signature !== signature) ambiguousIds.add(id);
    else if (!previous) uniqueById.set(id, { entry, signature });
  });
  const uniqueScoped = [...uniqueById.entries()]
    .filter(([id]) => !ambiguousIds.has(id))
    .map(([, item]) => item.entry);
  const outbound = uniqueScoped.find(entry => messageIdOf(entry.message, entry.id) === outboundId
    && isConfirmedHumanOutbound(entry.message));
  if (!outbound) return false;
  const outboundAt = timestampOf(outbound.message);
  if (!Number.isFinite(outboundAt)) return false;
  const cycleStart = cycleStartedAt == null ? outboundAt : Date.parse(cycleStartedAt);
  if (!Number.isFinite(cycleStart) || outboundAt < cycleStart) return false;

  return uniqueScoped.some(entry => {
    const message = entry?.message;
    return isGenuineClientInbound(message)
      && Number.isFinite(timestampOf(message))
      && timestampOf(message) > cycleStart;
  });
}